import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import GeoLocationComponent, {
  type GeoLocation,
} from 'components/GeoLocationComponent/GeoLocationComponent';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { useAors, useUpsertAor } from 'hooks/useAors';
import type { AreaOfResponsibility } from 'types/aor';
import { useAorText } from 'utils/aorTerminology';
import generateUUID from 'utils/sharedUtils/generateUUID';
import { validateGeoLocationSize } from 'utils/validateGeoLocationSize';
import { buildAorParentOptions } from '../utils/aorParentOptions';

type FormValues = {
  name: string;
  description: string;
  parent_item_id: string | null;
};

const EMPTY: FormValues = { name: '', description: '', parent_item_id: null };

function serverError(error: unknown): string | undefined {
  return (
    error as { response?: { data?: { ERROR?: string } } }
  )?.response?.data?.ERROR?.replace(/^BAD REQUEST exception Error: /, '');
}

/** Add or edit one AOR. `aor` null means add. */
export function AorEditorModal({
  open,
  onOpenChange,
  aor,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aor: AreaOfResponsibility | null;
}) {
  const aorText = useAorText();
  const upsertAor = useUpsertAor();
  const { data: aors = [] } = useAors();
  const [geoLocation, setGeoLocation] = useState<GeoLocation | undefined>();
  const [originalLocation, setOriginalLocation] = useState<string | null>(null);
  const parentOptions = useMemo(
    () => buildAorParentOptions(aors, aor?.id ?? null),
    [aors, aor?.id]
  );
  const { handleSubmit, control, reset, formState } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    reset(
      aor
        ? {
            name: aor.name,
            description: aor.description || '',
            parent_item_id: aor.parent_item_id ?? null,
          }
        : EMPTY
    );
    const loc = aor?.geolocation || undefined;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setGeoLocation(loc);
    setOriginalLocation(loc ? JSON.stringify(loc) : null);
  }, [open, aor, reset]);

  const title = aorText(
    `${aor ? 'Update' : 'Add'} Area of Responsibility (AOR)`
  );

  const onSubmit = async (values: FormValues) => {
    const current = geoLocation ? JSON.stringify(geoLocation) : null;
    let location: string | null | undefined;
    if (!aor || current !== originalLocation) {
      const { isValid, locationString } = validateGeoLocationSize(geoLocation);
      location = isValid ? locationString : null;
    }
    try {
      await upsertAor.mutateAsync({
        id: aor?.id || generateUUID(),
        group_guid: aor?.group_guid || '',
        name: values.name.trim(),
        description: values.description,
        location,
        category_id: null,
        parent_item_id: values.parent_item_id || null,
        cc_type: null,
      });
      onOpenChange(false);
    } catch (error) {
      toast.error(
        serverError(error) ||
          aorText('Could not save the Area of Responsibility.')
      );
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      widthClassName="tw:!max-w-3xl tw:sm:!max-w-3xl"
      formProps={{ onSubmit: handleSubmit(onSubmit) }}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={upsertAor.isPending}>
            {upsertAor.isPending
              ? 'Saving…'
              : aor
                ? 'Save'
                : aorText('Add AOR')}
          </Button>
        </>
      }
    >
      <FieldGroup>
        <Controller
          control={control}
          name="name"
          rules={{
            validate: (value) => value.trim().length > 0 || 'Name is required.',
          }}
          render={({ field }) => (
            <Field data-invalid={!!formState.errors.name || undefined}>
              <FieldLabel htmlFor="aor-name">Name</FieldLabel>
              <Input
                id="aor-name"
                placeholder="Enter name"
                aria-invalid={!!formState.errors.name || undefined}
                {...field}
              />
              <FieldError errors={[formState.errors.name]} />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <Field>
              <FieldLabel>Description</FieldLabel>
              <TiptapRichTextEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Enter description..."
                minHeight="120px"
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="parent_item_id"
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="aor-parent">
                {aorText('Parent AOR (optional)')}
              </FieldLabel>
              <SearchableSelect
                id="aor-parent"
                options={parentOptions}
                value={
                  parentOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                onChange={(next) => field.onChange(next?.value ?? null)}
                placeholder={aorText('No parent (top-level AOR)')}
                clearable
              />
              <FieldDescription>
                {aorText(
                  "Reports for this AOR are included as sections in its parent's reports."
                )}
              </FieldDescription>
            </Field>
          )}
        />
        <Field>
          <FieldLabel>Location</FieldLabel>
          <GeoLocationComponent
            location={geoLocation}
            setLocation={setGeoLocation}
            showFemaRegionMenu
          />
        </Field>
      </FieldGroup>
    </FormModal>
  );
}
